/**
 * Helper functions for fetching, filtering, and sampling axis values.
 *
 * Provides composable functions for the common pattern of:
 * 1. Getting all axis values (union or intersection)
 * 2. Applying selection range filtering
 * 3. Applying sampling to limit count
 */

import {sampleSorted} from '@wandb/weave/common/util/data';

import {getMediaMetadata} from '../../../../util/media';
import {TransformInput} from '../types';

// Debug logging flag
const DEBUG = false;
const log = DEBUG ? console.log : () => {};
const warn = DEBUG ? console.warn : () => {};

/**
 * Gets all axis values based on snap mode (union vs intersection).
 *
 * @param axis - The axis type ('run', 'step', or 'index')
 * @param runs - Array of runs
 * @param stepMeta - Step metadata
 * @param mediaKey - Media key to check for existence (for intersection mode)
 * @param snapToExistingStep - If true, use union; if false, use intersection
 * @returns Array of axis values
 *
 * @example
 * ```typescript
 * const allSteps = getAllAxisValues('step', runs, stepMeta, 'image', true);
 * // Returns all steps across all runs (union)
 * ```
 */
export function getAllAxisValues(
  axis: 'run' | 'step' | 'index',
  runs: TransformInput['runs'],
  stepMeta: TransformInput['stepMeta'],
  mediaKey: string,
  snapToExistingStep: boolean
): any[] {
  if (axis === 'run') {
    return runs;
  }

  if (axis === 'index') {
    return stepMeta.indices;
  }

  // Step axis: always return all available steps
  // snapToExistingStep controls behavior (whether to snap), not available data
  return stepMeta.sliderSteps ?? stepMeta.steps;
}

/**
 * Gets intersection of steps (only steps where ALL runs have the media key).
 *
 * @param runs - Array of runs
 * @param stepMeta - Step metadata
 * @param mediaKey - Media key to check for existence
 * @returns Array of steps that exist in ALL runs with data for the media key
 */
export function getIntersectionSteps(
  runs: TransformInput['runs'],
  stepMeta: TransformInput['stepMeta'],
  mediaKey: string
): number[] {
  if (runs.length === 0) {
    return [];
  }

  // Start with first run's steps where media key exists
  let intersectionSteps = stepMeta.runs[0].steps.filter(step => {
    const metadata = getMediaMetadata(runs[0].history, mediaKey, step);
    return metadata != null;
  });

  // Intersect with remaining runs
  for (let i = 1; i < runs.length; i++) {
    const run = runs[i];
    const runMeta = stepMeta.runs[i];

    intersectionSteps = intersectionSteps.filter(step => {
      if (!runMeta.stepSet.has(step)) {
        return false;
      }
      const metadata = getMediaMetadata(run.history, mediaKey, step);
      return metadata != null;
    });

    // Early exit if intersection is empty
    if (intersectionSteps.length === 0) {
      break;
    }
  }

  return intersectionSteps;
}

/**
 * Applies range filtering to values.
 * If filter removes all values, returns original values (defensive).
 *
 * @param axis - The axis type
 * @param values - Values to filter
 * @param min - Minimum value (inclusive)
 * @param max - Maximum value (inclusive)
 * @returns Filtered values, or original if filter would remove everything
 */
export function applyRangeFilter(
  axis: string,
  values: any[],
  min?: number,
  max?: number
): any[] {
  // Run axis doesn't support range filtering
  if (axis === 'run') {
    return values;
  }

  // No filter specified
  if (min === undefined || max === undefined) {
    return values;
  }

  const filtered = (values as number[]).filter(v => v >= min && v <= max);

  log(
    `[axisValueHelpers] Range filter [${min}, ${max}]: ${values.length} → ${filtered.length}`
  );

  // Defensive: if filter removed everything, ignore it
  if (filtered.length === 0) {
    warn(
      `[axisValueHelpers] Range filter [${min}, ${max}] excluded all ${axis} values! Ignoring filter.`
    );
    return values;
  }

  return filtered;
}

/**
 * Applies sampling to limit value count.
 *
 * @param axis - The axis type (needed to handle 'run' axis specially)
 * @param values - Values to sample
 * @param maxCount - Maximum number of values to return
 * @returns Sampled values, or original if count is below limit
 */
export function applySampling(
  axis: string,
  values: any[],
  maxCount?: number
): any[] {
  if (!maxCount || values.length <= maxCount) {
    return values;
  }

  // For run axis, just take the first maxCount runs (preserve order)
  // This matches the old layout behavior: runs.slice(0, maxSize)
  if (axis === 'run') {
    log(`[axisValueHelpers] Run axis sampling: ${values.length} → ${maxCount}`);
    return values.slice(0, maxCount);
  }

  // For numeric axes (step, index), use sampleSorted to evenly distribute
  const sampled = sampleSorted(values as number[], maxCount);

  log(`[axisValueHelpers] Sampling: ${values.length} → ${maxCount}`);

  return sampled as any;
}

/**
 * Gets axis values with filtering and sampling applied (convenience function).
 *
 * @param axis - The axis type
 * @param runs - Array of runs
 * @param stepMeta - Step metadata
 * @param mediaKey - Media key to check for existence
 * @param snapToExistingStep - If true, use union; if false, use intersection
 * @param minRange - Minimum range value (optional)
 * @param maxRange - Maximum range value (optional)
 * @param maxCount - Maximum number of values (optional)
 * @returns Object with all values and filtered/sampled values
 */
export function getAxisValuesWithOptions(
  axis: 'run' | 'step' | 'index',
  runs: TransformInput['runs'],
  stepMeta: TransformInput['stepMeta'],
  mediaKey: string,
  snapToExistingStep: boolean,
  minRange?: number,
  maxRange?: number,
  maxCount?: number
): {allValues: any[]; values: any[]} {
  // Get all values
  const allValues = getAllAxisValues(
    axis,
    runs,
    stepMeta,
    mediaKey,
    snapToExistingStep
  );

  // Apply filtering and sampling
  let values = applyRangeFilter(axis, allValues, minRange, maxRange);
  values = applySampling(axis, values, maxCount);

  return {allValues, values};
}
